/**
 * Vista 7 · Documento declarado — nivel 3. **La pantalla de trabajo del sistema.**
 *
 * Todo lo que decide qué se puede hacer aquí lo decide el servidor: `acciones`,
 * `puedeEmitir`, `estado`, `tono` y `etiqueta` vienen en la proyección
 * (`ARQ-4`). Esta vista no vuelve a clasificar nada; si lo hiciera, habría dos
 * verdades que pueden divergir, que es el defecto que Recuperos documenta
 * haber corregido.
 *
 * La línea **«Resuelto por»** es obligatoria y explícita: ante una auditoría la
 * pregunta es *¿con qué prueba cerraron esto?*, y la respuesta tiene que estar
 * en pantalla, no deducirse comparando tarjetas.
 */
import { useState } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle, StatusBadge } from '@mpfn/ui'
import { Callout, EmptyState, KeyValueGrid } from '@mpfn/patterns'
import { Ban, Download, FileWarning, Send } from 'lucide-react'
import { useAnularConsulta, useAnularRequerimiento, useDocumento, useEntidades } from '../api/consultas.js'
import { canalDe, etiquetaDe, pasoDe, useCatalogos } from '../api/catalogos.js'
import { urlDeArchivo } from '../api/cliente.js'
import type { Consulta, DocumentoDeclarado, Requerimiento } from '../api/tipos.js'
import { AcordeonRequerimiento } from '../componentes/AcordeonRequerimiento.js'
import { OficioDelRequerimiento } from '../componentes/OficioDelRequerimiento.js'
import { Datos } from '../componentes/Datos.js'
import { Pagina } from '../componentes/Pagina.js'
import { DialogoConMotivo } from '../componentes/DialogoConMotivo.js'
import { DialogoDerivacion } from '../componentes/DialogoDerivacion.js'
import { DialogoPaso } from '../componentes/DialogoPaso.js'
import { DialogoRespuesta } from '../componentes/DialogoRespuesta.js'
import { TarjetaConsulta } from '../componentes/TarjetaConsulta.js'
import { Breadcrumb } from '../componentes/Breadcrumb.js'
import { usePuede, useSesion } from '../sesion/contexto.js'
import { tienePermiso } from '../navegacion/permisos.js'
import { avisarError, avisarExito, plural } from '../lib/avisos.js'
import { fecha } from '../lib/formato.js'
import { DialogoEmision } from './DialogoEmision.js'

export function Documento({ id }: { id: string }) {
  const documento = useDocumento(id)

  return (
    <Pagina>
      <Datos estado={documento}>{(datos) => <Contenido documento={datos} />}</Datos>
    </Pagina>
  )
}

function Contenido({ documento }: { documento: DocumentoDeclarado }) {
  const catalogos = useCatalogos()
  const entidades = useEntidades()
  const puedeEmitir = usePuede('requerimiento.emitir')
  const puedeDerivar = usePuede('oci.derivar')

  const [emitiendo, setEmitiendo] = useState(false)
  const [derivando, setDerivando] = useState(false)

  const entidad = (entidades.data ?? []).find((item) => item.id === documento.entidadId)
  const vigentes = documento.requerimientos.filter((requerimiento) => !requerimiento.anulada)
  const ultimo = vigentes.at(-1)

  const causaDerivacion =
    documento.estado === 'irregular'
      ? ('documento_falso' as const)
      : documento.estado === 'no_acreditado'
        ? ('no_acreditado' as const)
        : undefined

  return (
    <>
      <Breadcrumb
        migas={[
          {
            etiqueta: 'Expediente',
            vista: 'expediente',
            parametros: { id: documento.expedienteId },
          },
          { etiqueta: documento.denominacion },
        ]}
      />

      <Card>
        <CardHeader className="sigfisc-documento__cabecera">
          <div>
            <CardTitle>{documento.denominacion}</CardTitle>
            <p className="sigfisc-nota">
              {etiquetaDe(catalogos.secciones, documento.seccion)}
              {entidad ? ` · ${entidad.nombre}` : documento.entidadTexto ? ` · ${documento.entidadTexto}` : ''}
            </p>
          </div>
          <StatusBadge tone={documento.tono} label={documento.etiqueta} />
        </CardHeader>
        <CardContent>
          <KeyValueGrid
            items={[
              {
                label: 'Legajo',
                value:
                  documento.legajoEvidenciaId !== undefined ? (
                    /*
                     * `Button` con `asChild`, no un `<a>` con una clase suelta.
                     *
                     * Antes llevaba solo `mpfn-control`, que da el contrato de
                     * FOCO y nada más —ni fondo, ni borde, ni altura—, así que
                     * se veía el enlace azul subrayado del navegador en medio
                     * de una rejilla de datos. `asChild` hace que el DS pinte
                     * sus recetas de botón sobre el propio `<a>`, con lo que
                     * sigue siendo una descarga real y no un `onClick`.
                     */
                    <Button variant="secondary" size="sm" asChild>
                      <a href={urlDeArchivo(documento.legajoEvidenciaId)} download>
                        <Download size={14} aria-hidden="true" />
                        Descargar
                      </a>
                    </Button>
                  ) : (
                    'No adjuntado'
                  ),
              },
              { label: 'Número', value: documento.numeroDocumento ?? '—' },
              {
                label: 'Requerimientos',
                value: `${String(vigentes.length)} emitidos · el siguiente sería el ${String(documento.ordinalSiguiente)}`,
              },
              {
                label: 'Última consulta abierta',
                value: ultimo ? fecha(ultimo.fechaEmision) : '—',
              },
            ]}
          />
        </CardContent>
      </Card>

      {documento.estado === 'vencido_sin_respuesta' && (
        <Callout tone="warning" title="Venció sin respuesta">
          Ninguna consulta de este requerimiento respondió dentro del plazo. Procede reiterar, o
          cerrar el ciclo si ya se agotaron los requerimientos previstos.
        </Callout>
      )}

      {documento.estado === 'irregular' && (
        <Callout tone="critical" title="Una entidad negó la autenticidad">
          Es un hallazgo sustantivo. Procede la derivación inmediata al Órgano de Control Interno.
        </Callout>
      )}

      {documento.estado === 'no_acreditado' && (
        <Callout tone="warning" title="No se pudo acreditar">
          Se agotaron los requerimientos sin obtener respuesta. Procede derivar al OCI por no
          acreditado.
        </Callout>
      )}

      {documento.estado === 'derivado_oci' && (
        <Callout tone="info" title="Derivado al Órgano de Control Interno">
          El caso ya salió del circuito de fiscalización.
        </Callout>
      )}

      {/* ── Acciones ──────────────────────────────────────────────────── */}
      <div className="sigfisc-acciones">
        {puedeEmitir && documento.acciones.some((accion) => accion === 'emitir_requerimiento' || accion === 'reiterar') && (
          <Button
            variant="primary"
            disabled={!documento.puedeEmitir}
            onClick={() => {
              setEmitiendo(true)
            }}
          >
            <Send size={16} aria-hidden="true" />
            {documento.ordinalSiguiente > 1
              ? `Reiterar · requerimiento ${String(documento.ordinalSiguiente)}`
              : 'Emitir requerimiento'}
          </Button>
        )}

        {puedeDerivar && causaDerivacion !== undefined && documento.estado !== 'derivado_oci' && (
          <Button
            variant="destructive"
            onClick={() => {
              setDerivando(true)
            }}
          >
            <FileWarning size={16} aria-hidden="true" />
            Derivar al OCI
          </Button>
        )}
      </div>

      {/* ── Requerimientos ────────────────────────────────────────────── */}
      {documento.requerimientos.length === 0 ? (
        <EmptyState
          icon={Send}
          title="Este documento aún no se ha requerido"
          description="Emitir un requerimiento abre una consulta por cada canal elegido."
          {...(puedeEmitir && documento.puedeEmitir
            ? {
                action: (
                  <Button
                    variant="primary"
                    onClick={() => {
                      setEmitiendo(true)
                    }}
                  >
                    Emitir requerimiento
                  </Button>
                ),
              }
            : {})}
        />
      ) : (
        // El vigente arriba: es donde está el trabajo. Los anteriores bajan,
        // colapsados pero presentes, porque son parte de la traza.
        documento.requerimientos
          .toReversed()
          .map((requerimiento) => (
            <PanelRequerimiento
              key={requerimiento.id}
              requerimiento={requerimiento}
              abiertoPorDefecto={requerimiento.id === ultimo?.id}
            />
          ))
      )}

      <DialogoEmision documento={documento} abierto={emitiendo} onCambio={setEmitiendo} />

      {causaDerivacion !== undefined && (
        <DialogoDerivacion
          documentoId={documento.id}
          denominacion={documento.denominacion}
          causa={causaDerivacion}
          abierto={derivando}
          onCambio={setDerivando}
        />
      )}
    </>
  )
}

function PanelRequerimiento({
  requerimiento,
  abiertoPorDefecto,
}: {
  requerimiento: Requerimiento
  abiertoPorDefecto: boolean
}) {
  const catalogos = useCatalogos()
  const puedeAnular = usePuede('requerimiento.anular')
  const anular = useAnularRequerimiento()
  const [anulando, setAnulando] = useState(false)

  const resolutoria = requerimiento.consultas.find(
    (consulta) => consulta.id === requerimiento.consultaResolutoriaId,
  )

  const etiquetaEstado = requerimiento.desenlace
    ? etiquetaDe(catalogos.desenlacesRequerimiento, requerimiento.desenlace)
    : 'En curso'
  const tono = requerimiento.desenlace
    ? requerimiento.desenlace === 'autentico'
      ? ('good' as const)
      : requerimiento.desenlace === 'no_autentico'
        ? ('critical' as const)
        : ('warning' as const)
    : ('info' as const)

  return (
    <AcordeonRequerimiento
      ordinal={requerimiento.ordinal}
      referencia={
        !requerimiento.oficio.lleva
          ? 'Consulta en línea'
          : requerimiento.numeroProvisional
            ? `Oficio ${requerimiento.numeroOficio} (provisional)`
            : `Oficio N.° ${requerimiento.numeroOficio}`
      }
      fechaEmision={requerimiento.fechaEmision}
      estadoEtiqueta={etiquetaEstado}
      tono={tono}
      abiertoPorDefecto={abiertoPorDefecto}
      anulada={requerimiento.anulada}
    >
      <OficioDelRequerimiento requerimiento={requerimiento} />

      <div className="sigfisc-consultas">
        {requerimiento.consultas.map((consulta) => (
          <TarjetaConsulta
            key={consulta.id}
            consulta={consulta}
            esResolutoria={consulta.id === requerimiento.consultaResolutoriaId}
            acciones={<AccionesDeConsulta consulta={consulta} requerimiento={requerimiento} />}
          />
        ))}
      </div>

      {resolutoria && (
        <p className="sigfisc-resuelto">
          <strong>Resuelto por:</strong> {resolutoria.canalEtiqueta} · {resolutoria.destinoTexto}
          {resolutoria.respuesta ? ` · ${fecha(resolutoria.respuesta.fechaRecepcion)}` : ''}
        </p>
      )}

      {puedeAnular && !requerimiento.anulada && !requerimiento.desenlace && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setAnulando(true)
            }}
          >
            <Ban size={14} aria-hidden="true" />
            Anular el requerimiento
          </Button>
          <DialogoConMotivo
            abierto={anulando}
            onCambio={setAnulando}
            titulo={`Anular el requerimiento ${String(requerimiento.ordinal)}`}
            descripcion={`Oficio ${requerimiento.numeroOficio}. Sus consultas dejarán de estar vivas.`}
            etiquetaConfirmar="Anular el requerimiento"
            cargando={anular.isPending}
            advertencia={
              <Callout tone="info" title="No consume ordinal">
                Un requerimiento anulado no gasta uno de los intentos disponibles: el siguiente
                volverá a ser el {requerimiento.ordinal}.
              </Callout>
            }
            onConfirmar={(motivo) => {
              anular.mutate(
                { requerimientoId: requerimiento.id, motivo },
                {
                  onSuccess: () => {
                    avisarExito('Requerimiento anulado.')
                    setAnulando(false)
                  },
                  onError: (error) => {
                    avisarError(error, 'No se pudo anular el requerimiento')
                  },
                },
              )
            }}
          />
        </>
      )}
    </AcordeonRequerimiento>
  )
}

function AccionesDeConsulta({
  consulta,
  requerimiento,
}: {
  consulta: Consulta
  requerimiento: Requerimiento
}) {
  const catalogos = useCatalogos()
  // La sesión entera, no `usePuede`: el permiso que hay que comprobar depende
  // del paso que toca, y eso se calcula más abajo. `tienePermiso` es una
  // función normal, así que no ata las reglas de los hooks.
  const { usuario } = useSesion()
  const puedeResponder = usePuede('respuesta.registrar')
  const puedeAnularConsulta = usePuede('consulta.anular')
  const anular = useAnularConsulta()

  const [registrandoPaso, setRegistrandoPaso] = useState(false)
  const [respondiendo, setRespondiendo] = useState(false)
  const [anulando, setAnulando] = useState(false)

  const viva = consulta.desenlace === undefined && !consulta.anulada
  if (!viva) return null

  /**
   * El permiso del paso que toca, **no uno fijo**.
   *
   * Antes se comprobaba siempre `consulta.paso.fiscalizacion`, con lo que a un
   * coordinador se le ofrecía «Registrar el paso 2» del canal físico —«Mesa de
   * Partes recibió el oficio»—, que es de otro actor: el botón existía y el
   * servidor respondía 403. Cada paso declara su actor y el catálogo sirve el
   * permiso que le corresponde (`ARQ-10`).
   */
  const siguiente =
    consulta.siguientePaso !== undefined
      ? pasoDe(catalogos, consulta.canal, consulta.siguientePaso)
      : undefined

  /** El paso que toca es el cierre formal del canal. */
  const pasoCierrePendiente =
    canalDe(catalogos, consulta.canal)?.pasoCierre === consulta.siguientePaso
  const cierraSinRespuesta = pasoCierrePendiente && consulta.respuesta === undefined
  const puedeRegistrarPasoCierre =
    siguiente !== undefined && tienePermiso(usuario, siguiente.permiso)

  const hermanasVivas = requerimiento.consultas.filter(
    (otra) => otra.id !== consulta.id && otra.desenlace === undefined && !otra.anulada,
  ).length

  /**
   * Lo que se envía es el oficio firmado: el primer paso de un canal que lo
   * lleva espera a que vuelva del trámite documentario. El servidor lo exige
   * igual (`OFICIO_SIN_FIRMAR`); aquí se dice antes de intentarlo.
   */
  const esperaOficio =
    consulta.siguientePaso === 1 &&
    consulta.canal !== 'consulta_web' &&
    requerimiento.oficio.lleva &&
    !requerimiento.oficio.firmado

  return (
    <>
      {esperaOficio && siguiente !== undefined && tienePermiso(usuario, siguiente.permiso) && (
        <>
          <Button variant="secondary" size="sm" disabled aria-describedby={`espera-oficio-${consulta.id}`}>
            Registrar el paso 1
          </Button>
          <span id={`espera-oficio-${consulta.id}`} className="sigfisc-nota">
            Primero registre el oficio firmado.
          </span>
        </>
      )}

      {!esperaOficio &&
        siguiente !== undefined &&
        consulta.siguientePaso !== undefined &&
        tienePermiso(usuario, siguiente.permiso) && (
          <>
            {/* El paso de cierre se anuncia por lo que HACE. «Registrar el
                paso 9» no deja ver que cierra la consulta y renuncia a la
                respuesta; el propio botón lo dice antes de abrir el diálogo. */}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setRegistrandoPaso(true)
              }}
            >
              {cierraSinRespuesta
                ? 'Cerrar sin respuesta'
                : `Registrar el paso ${String(consulta.siguientePaso)}`}
            </Button>
            <DialogoPaso
              consultaId={consulta.id}
              canal={consulta.canal}
              paso={consulta.siguientePaso}
              // Una respuesta concluyente registrada desde esta pantalla ya
              // incorpora el cierre. Si solo consta una no concluyente, este
              // paso formaliza el fin; sin respuesta, cierra como «Sin respuesta».
              tieneRespuesta={consulta.respuesta !== undefined}
              abierto={registrandoPaso}
              onCambio={setRegistrandoPaso}
            />
          </>
        )}

      {puedeResponder && consulta.respuesta === undefined && (
        <>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setRespondiendo(true)
            }}
          >
            {pasoCierrePendiente ? 'Finalizar con respuesta' : 'Registrar respuesta'}
          </Button>
          <DialogoRespuesta
            consulta={consulta}
            hermanasVivas={hermanasVivas}
            pasoCierrePendiente={pasoCierrePendiente}
            puedeRegistrarPasoCierre={puedeRegistrarPasoCierre}
            abierto={respondiendo}
            onCambio={setRespondiendo}
          />
        </>
      )}

      {puedeAnularConsulta && (
        <>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Anular la consulta por ${consulta.canalEtiqueta}`}
            onClick={() => {
              setAnulando(true)
            }}
          >
            <Ban size={14} aria-hidden="true" />
          </Button>
          <DialogoConMotivo
            abierto={anulando}
            onCambio={setAnulando}
            titulo="Anular la consulta"
            descripcion={`${consulta.canalEtiqueta} · ${consulta.destinoTexto}`}
            etiquetaConfirmar="Anular la consulta"
            cargando={anular.isPending}
            advertencia={
              hermanasVivas === 0 ? (
                <Callout tone="warning" title="Es la única consulta viva">
                  Al anularla, el requerimiento se queda sin ningún canal esperando respuesta.
                </Callout>
              ) : (
                <Callout tone="info" title="Quedan otras consultas abiertas">
                  {plural(hermanasVivas, 'consulta seguirá', 'consultas seguirán')} esperando por
                  otros canales.
                </Callout>
              )
            }
            onConfirmar={(motivo) => {
              anular.mutate(
                { consultaId: consulta.id, motivo },
                {
                  onSuccess: () => {
                    avisarExito('Consulta anulada.')
                    setAnulando(false)
                  },
                  onError: (error) => {
                    avisarError(error, 'No se pudo anular la consulta')
                  },
                },
              )
            }}
          />
        </>
      )}
    </>
  )
}
